import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { GET_CANCELLATION_CHARGES, WAIVE_CANCELLATION_CHARGE } from "./graphql";
import { inputCls, rupees } from "./controls";

const PAGE = 50;

const VIA = {
  REFUND: "kept from refund",
  WALLET_REFUND: "kept from wallet refund",
  WALLET: "wallet",
  PAYMENT_LINK: "payment link",
  NEXT_BOOKING: "next booking",
  CREDIT: "wallet credit",
};

const STATUS = {
  SHADOW: { label: "Would charge", cls: "bg-amber-100 text-amber-800" },
  FREE: { label: "Free", cls: "bg-gray-100 text-gray-600" },
  EXEMPT: { label: "Exempt", cls: "bg-emerald-100 text-emerald-800" },
  WAIVED: { label: "Waived", cls: "bg-violet-100 text-violet-800" },
  PENDING: { label: "Owed", cls: "bg-orange-100 text-orange-800" },
  BILLED: { label: "On next booking", cls: "bg-indigo-100 text-indigo-800" },
  COLLECTED: { label: "Collected", cls: "bg-emerald-100 text-emerald-800" },
  WRITTEN_OFF: { label: "Written off", cls: "bg-gray-100 text-gray-600" },
  CREDITED: { label: "Credited", cls: "bg-sky-100 text-sky-800" },
};

export const StatusChip = ({ row }) => {
  if (row.kind === "CREDIT" && row.status === "PENDING") return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-800">Crediting</span>;
  const s = row.kind === "CREDIT" && row.status === "SHADOW" ? { label: "Would credit", cls: "bg-sky-100 text-sky-800" } : STATUS[row.status] || { label: row.status, cls: "bg-gray-100" };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>{s.label}</span>;
};

/** Waive one fee with a reason. Used here and in the booking's details. */
export const WaiveButton = ({ row, onDone }) => {
  const [waive, { loading }] = useMutation(WAIVE_CANCELLATION_CHARGE);
  if (row.kind !== "FEE" || !["SHADOW", "PENDING"].includes(row.status) || !(row.finalAmount > 0)) return null;
  const go = async () => {
    const reason = window.prompt(`Waive the ${rupees(row.finalAmount)} fee on ${row.bookingId}? Say why:`);
    if (!reason || !reason.trim()) return;
    const { data } = await waive({ variables: { id: row.id, reason: reason.trim() } });
    const r = data?.waiveCancellationCharge;
    if (r?.status !== "SUCCESS") window.alert(r?.message || "Could not waive the fee");
    else onDone && onDone(r.charge);
  };
  return (
    <button type="button" disabled={loading} onClick={go} className="px-2 py-1 rounded-lg border border-violet-200 text-violet-700 text-xs font-semibold hover:bg-violet-50 disabled:opacity-50">
      {loading ? "Waiving…" : "Waive"}
    </button>
  );
};

const Stat = ({ label, value, sub, tone = "text-gray-900" }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-semibold mt-0.5 ${tone}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const Breakdown = ({ title, rows }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
    {!rows?.length && <p className="text-sm text-gray-400">None</p>}
    {(rows || []).map((r) => (
      <div key={r.key} className="flex justify-between text-sm py-1 border-t border-gray-50 first:border-t-0">
        <span className="text-gray-700">{r.label}</span>
        <span className="text-gray-500">{r.count} · {rupees(r.amount)}</span>
      </div>
    ))}
  </div>
);

const FACT_LABELS = {
  bookingType: "Booking type",
  hoursBefore: "Hours before slot",
  minutesAfterBooking: "Minutes after booking",
  beauticianStatus: "Beautician",
  lateMinutes: "Beautician late (min)",
  actor: "Cancelled by",
};

const ChargesRecord = () => {
  const [f, setF] = useState({ from: dayjs().subtract(30, "day").format("YYYY-MM-DD"), to: dayjs().format("YYYY-MM-DD"), kind: "", status: "", event: "", bookingType: "", search: "", chargeableOnly: false });
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(null);
  const filter = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== false));
  const { data, loading, refetch } = useQuery(GET_CANCELLATION_CHARGES, { variables: { filter, limit: PAGE, offset }, fetchPolicy: "cache-and-network" });
  const res = data?.getCancellationCharges;
  const s = res?.summary;
  const rows = res?.rows || [];
  const set = (k) => (e) => { setOffset(0); setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value })); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2 bg-white rounded-xl border border-gray-100 shadow-sm p-3 text-xs text-gray-600">
        <label>From<input type="date" value={f.from} onChange={set("from")} className={`${inputCls} block mt-0.5`} /></label>
        <label>To<input type="date" value={f.to} onChange={set("to")} className={`${inputCls} block mt-0.5`} /></label>
        <label>Type<select value={f.kind} onChange={set("kind")} className={`${inputCls} block mt-0.5`}><option value="">Fees &amp; credits</option><option value="FEE">Fees</option><option value="CREDIT">Credits</option></select></label>
        <label>Status<select value={f.status} onChange={set("status")} className={`${inputCls} block mt-0.5`}><option value="">Any</option>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></label>
        <label>What happened<select value={f.event} onChange={set("event")} className={`${inputCls} block mt-0.5`}>
          <option value="">Anything</option>
          <option value="CUSTOMER_CANCEL">Customer cancelled</option><option value="NO_SHOW">Customer no-show</option><option value="RESCHEDULE">Rescheduled</option>
          <option value="BARBERA_CANCEL">Barbera cancelled</option><option value="BEAUTICIAN_NO_SHOW">Beautician no-show</option><option value="BEAUTICIAN_LATE">Beautician late</option>
        </select></label>
        <label>Booking<select value={f.bookingType} onChange={set("bookingType")} className={`${inputCls} block mt-0.5`}><option value="">All</option><option value="STANDARD">Standard</option><option value="BRIDAL">Bridal</option><option value="PACKAGE">Package</option></select></label>
        <label>Booking ID<input value={f.search} onChange={set("search")} placeholder="261028-…" className={`${inputCls} block mt-0.5 w-32`} /></label>
        <label className="inline-flex items-center gap-1 pb-2"><input type="checkbox" checked={f.chargeableOnly} onChange={set("chargeableOnly")} /> Late ones only</label>
      </div>

      {res?.status === "ERROR" && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{res.message}</div>}

      {s && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Would charge" value={rupees(s.wouldCharge)} sub={`${s.wouldChargeCount} fees`} tone="text-amber-700" />
            <Stat label="Waived by exemptions" value={rupees(s.exemptAmount)} sub={`${s.exemptCount} late cancellations`} tone="text-emerald-700" />
            <Stat label="Waived by support" value={rupees(s.waivedAmount)} sub={`${s.waivedCount} fees`} tone="text-violet-700" />
            <Stat label="Barbera credits" value={rupees(s.creditAmount)} sub={`${s.credits} customers`} tone="text-sky-700" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Breakdown title="Late cancellations by rule" rows={s.byRule} />
            <Breakdown title="Why fees were waived" rows={s.byExemption} />
          </div>
          {(s.collected > 0 || s.pendingAmount > 0 || s.billedAmount > 0 || s.writtenOffAmount > 0 || s.creditedAmount > 0) && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Stat label="Collected" value={rupees(s.collected)} sub={Object.entries(s.collectedBy || {}).map(([k, v]) => `${VIA[k] || k} ${rupees(v)}`).join(" · ") || "—"} tone="text-emerald-700" />
              <Stat label="Owed by customers" value={rupees(s.pendingAmount)} sub={`${s.pendingCount} fees`} tone="text-orange-700" />
              <Stat label="On next bookings" value={rupees(s.billedAmount)} />
              <Stat label="Written off" value={rupees(s.writtenOffAmount)} tone="text-gray-500" />
              <Stat label="Credited to customers" value={rupees(s.creditedAmount)} tone="text-sky-700" />
            </div>
          )}
          <p className="text-xs text-gray-400">{s.cancellations} cancellations judged · {s.free} free (outside every window) · {s.chargeable} late</p>
        </>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 text-left">
            <tr>
              <th className="px-3 py-2">When</th><th className="px-3 py-2">Booking</th><th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">What happened</th><th className="px-3 py-2">Rule</th><th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Status</th><th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && !rows.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>}
            {!loading && !rows.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">Nothing recorded for these filters. Cancellations are recorded once the policy is in Shadow.</td></tr>}
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{dayjs(r.cancelledAt).format("DD MMM, h:mm A")}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.bookingId}<span className="block font-sans text-[11px] text-gray-400">{r.bookingType?.toLowerCase()} · {rupees(r.bookingValue)}</span></td>
                  <td className="px-3 py-2">{r.customerName || "—"}<span className="block text-[11px] text-gray-400">{r.phone}</span></td>
                  <td className="px-3 py-2">{r.eventLabel}</td>
                  <td className="px-3 py-2">{r.ruleLabel}{r.exemptionLabel && <span className="block text-[11px] text-emerald-700">{r.exemptionLabel}</span>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.amount !== r.finalAmount && <span className="text-xs text-gray-400 line-through mr-1">{rupees(r.amount)}</span>}
                    <span className={r.kind === "CREDIT" ? "text-sky-700 font-semibold" : "font-semibold"}>{r.kind === "CREDIT" ? "+" : ""}{rupees(r.finalAmount)}</span>
                  </td>
                  <td className="px-3 py-2"><StatusChip row={r} /></td>
                  <td className="px-3 py-2 text-gray-400">{open === r.id ? <MdExpandLess /> : <MdExpandMore />}</td>
                </tr>
                {open === r.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={8} className="px-4 py-3">
                      <div className="grid md:grid-cols-[1fr_260px] gap-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">How it was worked out (policy v{r.policyVersion})</p>
                          <ul className="list-disc pl-4 text-sm text-gray-700 space-y-0.5">{r.explanation.map((l, i) => <li key={i}>{l}</li>)}</ul>
                          {r.predatesPolicy && <p className="text-xs text-amber-700 mt-2">Booked before any policy was saved - judged by the current one.</p>}
                          {(r.collections || []).length > 0 && (
                            <div className="mt-2 text-xs text-gray-700">
                              <p className="font-semibold text-gray-500 uppercase mb-0.5">Collected</p>
                              {r.collections.map((c, i) => <p key={i}>{rupees(c.amount)} · {VIA[c.via] || c.via} · {dayjs(c.at).format("DD MMM, h:mm A")}{c.ref ? ` · ${c.ref}` : ""}</p>)}
                            </div>
                          )}
                          {r.status === "PENDING" && r.kind === "FEE" && <p className="text-xs text-orange-700 mt-2">Owed: {rupees(r.finalAmount - (r.collectedAmount || 0))}{r.paymentLinkUrl ? <> · <a className="underline" href={r.paymentLinkUrl} target="_blank" rel="noreferrer">payment link</a></> : ""}</p>}
                          {r.status === "WAIVED" && <p className="text-xs text-violet-700 mt-2">Waived by {r.waivedBy || "an admin"} on {dayjs(r.waivedAt).format("DD MMM, h:mm A")}: {r.waiveReason}</p>}
                        </div>
                        <div className="space-y-1 text-xs">
                          {Object.entries(FACT_LABELS).map(([k, label]) => (r.facts?.[k] !== undefined && r.facts?.[k] !== null) && (
                            <div key={k} className="flex justify-between"><span className="text-gray-500">{label}</span><span className="text-gray-800">{String(r.facts[k]).replace(/_/g, " ").toLowerCase()}</span></div>
                          ))}
                          <div className="pt-2" onClick={(e) => e.stopPropagation()}><WaiveButton row={r} onDone={() => refetch()} /></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {res?.total > PAGE && (
        <div className="flex items-center justify-end gap-2 text-sm text-gray-300">
          <span>{offset + 1}–{Math.min(offset + PAGE, res.total)} of {res.total}</span>
          <button type="button" disabled={offset === 0} onClick={() => setOffset(offset - PAGE)} className="px-3 py-1 rounded-lg bg-white/10 disabled:opacity-40">Previous</button>
          <button type="button" disabled={offset + PAGE >= res.total} onClick={() => setOffset(offset + PAGE)} className="px-3 py-1 rounded-lg bg-white/10 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
};

export default ChargesRecord;
